import type { ReactNode } from "react";
import { SelectionIndicator, Tab, TabList, TabPanel, Tabs as AriaTabs, type Key } from "react-aria-components";
import { cx } from "../cx.ts";

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number;
  icon?: ReactNode;
  /** Stip naast het label: er zijn niet-opgeslagen wijzigingen op deze tab. */
  dirty?: boolean;
  content: ReactNode;
  isDisabled?: boolean;
}

/**
 * Tabs met een onderstreping die met een veer meeschuift naar de actieve tab.
 * `sticky` laat de tabbalk onder de topbalk plakken; `prefix` staat links in die balk (bijvoorbeeld een mini-titel).
 */
export function Tabs({
  items,
  selectedKey,
  onSelectionChange,
  label,
  sticky,
  prefix,
  stuck,
  dirtyLabel,
}: {
  items: TabItem[];
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
  label: string;
  sticky?: boolean;
  prefix?: ReactNode;
  stuck?: boolean;
  dirtyLabel?: string;
}) {
  const list = (
    <TabList className="ui-tablist" aria-label={label}>
      {items.map((t) => (
        <Tab key={t.id} id={t.id} className="ui-tab" isDisabled={t.isDisabled}>
          {t.icon}
          <span>{t.label}</span>
          {t.count != null ? <span className="ui-tab__count">{t.count}</span> : null}
          {t.dirty ? <span className="ui-tab__dirty" role="img" aria-label={dirtyLabel} /> : null}
          <SelectionIndicator className="ui-tab__indicator" />
        </Tab>
      ))}
    </TabList>
  );
  return (
    <AriaTabs className="ui-tabs" selectedKey={selectedKey} onSelectionChange={onSelectionChange ? (k: Key) => onSelectionChange(String(k)) : undefined}>
      {sticky || prefix ? (
        <div className={cx("ui-tabs__bar", sticky && "ui-tabs__bar--sticky")} data-stuck={stuck || undefined}>
          {prefix ? <div className="ui-tabs__prefix">{prefix}</div> : null}
          {list}
        </div>
      ) : (
        list
      )}
      {items.map((t) => (
        <TabPanel key={t.id} id={t.id} className="ui-tabpanel">
          {t.content}
        </TabPanel>
      ))}
    </AriaTabs>
  );
}
